import { useCallback, useEffect, useRef, useState } from "react";

import type { CameraCatalogEntry } from "../modules/teleop-state";
import type { TeleopCommands } from "../modules/teleop-commands";
import type { VideoApi } from "../modules/teleop-session";

export interface UseCameraBootstrapOptions {
  cameras: CameraCatalogEntry[];
  commands: TeleopCommands | null;
  sessionReady: boolean;
  configReady: boolean;
  videoApi: VideoApi | null;
  setStatus: (text: string) => void;
  setSlotEnabled: (slot: number, enabled: boolean) => void;
}

export interface UseCameraBootstrapResult {
  cameraEnabled: boolean[];
  handleCameraToggle: (mount: string, slot: number, enabled: boolean) => Promise<boolean>;
}

/**
 * Owns the per-slot camera on/off state (kept in sync with the real per-slot stream,
 * not just this tab's own toggle clicks) and the one-time seed of the local rendering
 * gate from robot-config's declared defaults. Keeps this UI orchestration out of the
 * page component.
 */
export function useCameraBootstrap({
  cameras,
  commands,
  sessionReady,
  configReady,
  videoApi,
  setStatus,
  setSlotEnabled,
}: UseCameraBootstrapOptions): UseCameraBootstrapResult {
  const [cameraEnabled, setCameraEnabled] = useState<boolean[]>([]);
  const bootstrappedRef = useRef(false);

  // Size the per-slot enabled array from the catalog whenever it changes, seeding a
  // slot's initial guess from robot-config's declared default ONLY the first time we
  // ever see it (`prev[i] === undefined`). Once the reconcile effect below has set a
  // slot from the real stream, that value must stick — robot-config's `enabled` is a
  // stale boot-time default (see the seed effect further down), and this effect isn't
  // ordered against the reconcile one: if it ran after, unconditionally trusting the
  // catalog here would stomp a freshly-connected operator's correct "off" right back
  // to a stale "on", showing the toggle lit with no video ever arriving to match it.
  useEffect(() => {
    if (!cameras.length) {
      setCameraEnabled([]);
      bootstrappedRef.current = false;
      return;
    }
    const maxSlot = Math.max(...cameras.map((c) => c.channel));
    setCameraEnabled((prev) => {
      const next = Array.from({ length: maxSlot + 1 }, (_, i) => {
        if (prev[i] !== undefined) return prev[i];
        const cam = cameras.find((c) => c.channel === i);
        return cam ? Boolean(cam.enabled) : false;
      });
      return next;
    });
    videoApi?.ensureSlots?.(maxSlot);
  }, [cameras, videoApi]);

  // Reconcile with the actual per-slot stream, which mediasoup keeps in sync across
  // every operator (consumerClosed/newConsumer are broadcast to the whole room). Without
  // this, `cameraEnabled` only reflects this tab's own toggle clicks: another operator
  // turning a camera off leaves the toggle/overlay here still showing "on" over a blank
  // video, and the next click here toggles from that stale state instead of the real one.
  // Subscribed directly (rather than derived from a combined streams snapshot) so each
  // event only ever touches its own slot — an unrelated slot's stream churning while this
  // one is mid-toggle (optimistic, real consumer not attached yet) must not reconcile it.
  useEffect(() => {
    if (!videoApi?.onSlotStream) return undefined;
    return videoApi.onSlotStream((slot, stream) => {
      // A slot's stream defaults to an empty (non-null) MediaStream before any consumer
      // has ever attached, so "has a track" — not "is non-null" — is the actual live
      // signal; see video-pipeline.ts's own `countVideoTracks`.
      const hasStream = (stream?.getVideoTracks().length ?? 0) > 0;
      setCameraEnabled((prev) => {
        if (prev[slot] === hasStream) return prev;
        const next = [...prev];
        next[slot] = hasStream;
        return next;
      });
    });
  }, [videoApi]);

  // One-shot seed of the local keyframe/restore gate (`setSlotEnabled`) from
  // robot-config's declared default, once session + config are ready.
  //
  // Deliberately does NOT (re-)send a `camera-control` enable command here. A camera
  // the robot already had producing at boot is already attached by room-consumers.ts
  // off the `join` response — well before this effect ever runs, since `join` happens
  // before robot-config is even requested (a step later, once the chat DataChannel
  // opens — see connect-phase.ts). robot-config's `enabled` is that static boot-time
  // default, not a live per-camera status feed: it goes stale the instant ANY operator
  // toggles the camera, and robot-config.ts's own ingest() ignores every robot-config
  // message after the first anyway. Sending `camera(mount, true)` from here on every
  // fresh join/reload — including a DIFFERENT operator's reload — would resurrect a
  // camera someone had deliberately turned off, fighting their explicit intent instead
  // of just reflecting reality. The toggle already reflects the real per-slot stream
  // (see the reconcile effect above), so a camera that isn't actually live shows
  // correctly as off and any operator can turn it on by hand.
  useEffect(() => {
    if (!sessionReady || !configReady || !cameras.length) {
      if (!sessionReady) bootstrappedRef.current = false;
      return;
    }
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    for (const cam of cameras) {
      setSlotEnabled(cam.channel, cam.enabled);
    }
  }, [setSlotEnabled, configReady, cameras, sessionReady]);

  const handleCameraToggle = useCallback(
    async (mount: string, slot: number, enabled: boolean): Promise<boolean> => {
      if (!sessionReady) {
        setStatus("DataChannel не готов — камера не переключена");
        return false;
      }
      setCameraEnabled((prev) => {
        const next = [...prev];
        next[slot] = enabled;
        return next;
      });
      setSlotEnabled(slot, enabled);
      setStatus(enabled ? `Включаем камеру ${mount}…` : `Останавливаем камеру ${mount}…`);
      try {
        commands?.camera(mount, enabled);
        setStatus(
          enabled
            ? `Камера ${mount}: запрос на включение отправлен`
            : `Камера ${mount}: запрос на выключение отправлен`
        );
        return true;
      } catch (e) {
        setCameraEnabled((prev) => {
          const next = [...prev];
          next[slot] = !enabled;
          return next;
        });
        setSlotEnabled(slot, !enabled);
        setStatus(`Камера ${mount}: ошибка — ${e}`);
        return false;
      }
    },
    [sessionReady, commands, setStatus, setSlotEnabled]
  );

  return { cameraEnabled, handleCameraToggle };
}
